import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import ExportsActions from 'pages/Exports/components/ExportsActions'
import { useLazyQuery, useMutation } from '@apollo/client'
import { displayErrors, DoLazyQuery, hasNoError } from 'globals/graphql'
import DataTable from 'components/DataTable'
import { Table } from 'antd'
import { ExportsDataTableWrapper } from 'pages/Exports/styles'
import { format } from 'date-fns'
import { Domiciliation } from 'globals/models/Domiciliation'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { QUIETS_LIST, QuietsListVariables, QuietsListResult } from 'pages/Exports/components/Quiets/queries'
import { SetState } from 'globals'
import Loader, { LoaderContainer } from 'components/Loader'
import { QUIETS_EXPORT, QuietsExportResult } from './mutations'

const Column = Table.Column

interface IProps {
  onShowExportConfirmation: () => void
}

const Quiets = (props: IProps) => {
  const { onShowExportConfirmation } = props
  const { t } = useTranslation()
  const [quiets, setQuiets] = useState<Domiciliation[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [noMoreQuiets, setNoMoreQuiets] = useState(false)

  const [doListQuiets, quietsListQuery] =
    useLazyQuery<QuietsListResult, QuietsListVariables>(
        QUIETS_LIST,
        {
          ...displayErrors,
          onCompleted: (quietsList) => {
            displayErrors.onCompleted(quietsList)

            if (quietsList.quietsList.length > 0) {
              setQuiets((cancellations) =>
                cancellations.concat(quietsList.quietsList)
              )
            } else {
              setNoMoreQuiets(true)
            }
          }
        }
    )

  const [quietsExport] = useMutation<QuietsExportResult>(QUIETS_EXPORT, displayErrors)

  const onExport = (onShowExportConfirmation: () => void) => () =>
    quietsExport().then(
        hasNoError(() => onShowExportConfirmation())
    )

  const [sentryRef] = useInfiniteScroll({
    loading: quietsListQuery.loading,
    hasNextPage: !noMoreQuiets,
    delayInMs: 1000,
    rootMargin: '0px 0px 400px 0px',
    onLoadMore: fetchMoreQuiets(currentPage, doListQuiets, setCurrentPage)
  })

  return (
    <>
      <ExportsActions
        onExport={onExport(onShowExportConfirmation)}
        noRangeDatePicker
      />

      {quiets.length > 0 &&
      <ExportsDataTableWrapper>
        <DataTable
          data={quiets}
          pageSize={10000}
        >
          <Column
            key="id"
            dataIndex="id"
            title={t('global.id')}
            className="small hide-mobile"
          />

          <Column
            key="company"
            dataIndex={['society', 'name']}
            title={
              <>
                <span>{t('global.company-name')}</span>
                <span className="title-mobile">{t('global.name')}</span>
              </>
            }
          />

          <Column
            key="siret"
            dataIndex="siretOrRna"
            title={t('global.siret')}
            className="hide-mobile"
          />

          <Column
            key="lastConsulting"
            dataIndex="lastConsulting"
            title={
              <>
                <span>{t('exports.quiets.last-mail-consulting')}</span>
                <span className="title-mobile">{t('exports.quiets.last-consulting')}</span>
              </>
            }
            render={(lastConsulting) => lastConsulting && format(new Date(lastConsulting), 'dd/MM/yyyy')}
          />
        </DataTable>
      </ExportsDataTableWrapper>
      }

      {!noMoreQuiets &&
        <div>
          <LoaderContainer ref={sentryRef}>
            <Loader />
          </LoaderContainer>
        </div>
      }
    </>
  )
}

const fetchMoreQuiets = (
    page: number,
    fetch: DoLazyQuery<QuietsListVariables>,
    setPage: SetState<number>
) => () => {
  fetch({ variables: { page: page } })
  setPage(page + 1)
}

export default Quiets
